import React from "react";
import { User, Trash2, Plus } from "lucide-react";

/**
 * "O mně" editor — name/role/intro fully editable + extra highlight boxes
 * (add/remove/edit). The "Lokalita" box (Google Maps link) stays hardcoded
 * in About.jsx since it's tied to the real STUDIO_ADDRESS data.
 */
export default function AboutEditor({ about, onChange }) {
  const data = about || { name: "", role: "", intro: "", boxes: [] };
  const boxes = Array.isArray(data.boxes) ? data.boxes : [];
  const setField = (key, value) => onChange({ ...data, [key]: value });
  const setBoxAt = (i, patch) => onChange({ ...data, boxes: boxes.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  const removeBox = (i) => onChange({ ...data, boxes: boxes.filter((_, idx) => idx !== i) });
  const addBox = () => onChange({ ...data, boxes: [...boxes, { title: "", value: "" }] });

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="section-about">
      <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-1 flex items-center gap-2">
        <User className="w-3.5 h-3.5" /> O mně
      </div>
      <p className="text-xs text-apex-cream/60 mb-4">
        Jméno, role a úvodní text. Box &bdquo;Lokalita&ldquo; (s odkazem na Google Mapy) je pevný — zde upravujete jen doplňkové boxy (např. Přípravky, Specializace).
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Jméno</div>
          <input
            type="text"
            value={data.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
            data-testid="about-input-name"
          />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Role / štítek</div>
          <input
            type="text"
            value={data.role || ""}
            onChange={(e) => setField("role", e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
            data-testid="about-input-role"
          />
        </label>
      </div>
      <label className="block mb-4">
        <div className="text-xs text-apex-cream/70 mb-1">Úvodní text</div>
        <textarea
          value={data.intro || ""}
          rows={3}
          onChange={(e) => setField("intro", e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm resize-y focus:outline-none focus:border-apex-gold/60"
          data-testid="about-input-intro"
        />
      </label>

      <div className="text-xs text-apex-cream/70 mb-2">Doplňkové boxy</div>
      <div className="space-y-2">
        {boxes.length === 0 && (
          <div className="text-xs italic text-apex-cream/45 px-2 py-1.5">
            Žádné doplňkové boxy — přidejte kliknutím na &bdquo;+ Přidat box&ldquo;.
          </div>
        )}
        {boxes.map((b, i) => (
          <div key={i} className="flex gap-2 items-start">
            <input
              type="text"
              value={b.title}
              onChange={(e) => setBoxAt(i, { title: e.target.value })}
              placeholder="Titulek (např. Přípravky)"
              className="flex-1 px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
              data-testid={`about-box-${i}-title`}
            />
            <input
              type="text"
              value={b.value}
              onChange={(e) => setBoxAt(i, { value: e.target.value })}
              placeholder="Hodnota (např. Auto Finesse (UK))"
              className="flex-1 px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
              data-testid={`about-box-${i}-value`}
            />
            <button
              type="button"
              onClick={() => removeBox(i)}
              className="px-2 py-2 rounded-lg border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10"
              data-testid={`about-box-${i}-remove`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addBox}
        className="mt-3 text-xs px-3 py-1.5 rounded-lg border border-apex-gold/40 text-apex-gold hover:bg-apex-gold/10 inline-flex items-center gap-1.5"
        data-testid="about-box-add"
      >
        <Plus className="w-3.5 h-3.5" /> Přidat box
      </button>
    </div>
  );
}
